import React, { useState, useRef } from 'react'
import { t, Trans } from '@lingui/macro'

import { useCreateWorkspaceMutation } from './createWorkspaceMutation.codegen'
import {
  ButtonPrimary,
  ButtonClean,
  ButtonOutlinePrimary
} from 'front-end/src/components'
import { Modal } from '../common/modal/Modal'
import { FormRow } from '../common/form/Form'
import Label from '../common/form/label/Label'
import Input from '../common/form/input/Input'
import { navigateTo } from '../../app/util/appHistory'
import IconLock from '../common/icon/IconLock'
import IconLockOpen from '../common/icon/IconLockOpen'
import HorizontalChoiceSelector from '../common/horizontal-choice-selector/HorizontalChoiceSelector'
import { AccessibilityLabel } from '../common/AccessibilityLabel'
import TagSelect from 'front-end/src/components/common/tag/TagSelect'

interface ICreateWorkspaceButtonModal {
  hideNavigation: () => void
}

export const CreateWorkspaceButtonModal = ({
  hideNavigation
}: ICreateWorkspaceButtonModal) => {
  const [modalShown, setModalShown] = useState(false)
  const [newWorkspaceIsOpen, setNewWorkspaceIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [tags, setTags] = useState({
    existingTagIds: [],
    newTagNames: []
  })
  const inputEl = useRef(null)
  const [createWorkspace] = useCreateWorkspaceMutation()
  const options = [
    {
      value: true,
      header: (
        <>
          <IconLockOpen />
          <br />
          <Trans>Open</Trans>
        </>
      ),
      description: (
        <div className="description">
          <span>
            <Trans>
              Anyone in your organisation will be allowed to view published
              resources
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: false,
      header: (
        <>
          <IconLock />
          <br />
          <Trans>Private</Trans>
        </>
      ),
      description: (
        <div className="description">
          <span>
            <Trans>
              Only invited members will be allowed to view published resources
            </Trans>
          </span>
        </div>
      )
    }
  ]

  const changeTags = (tags) => {
    const existingTagIds = tags.filter((tag) => tag.id).map((tag) => tag.id)
    const newTagNames = tags.filter((tag) => !tag.id).map((tag) => tag.name)

    setTags({
      existingTagIds,
      newTagNames
    })
  }

  return (
    <>
      <ButtonOutlinePrimary
        size="xs"
        className="Navigation__action Navigation__createWorkspace"
        title={t`Create a new Workspace`}
        onClick={() => {
          setModalShown(true)
        }}
      >
        <Trans>Create</Trans>
      </ButtonOutlinePrimary>
      <Modal
        newDesign
        className="CreateWorkspaceModal"
        header={t`Create a new Workspace`}
        show={modalShown}
        onHide={() => {
          setModalShown(false)
        }}
        controls={[
          <ButtonClean
            key={1}
            onClick={() => {
              setModalShown(false)
            }}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>,
          <ButtonPrimary
            key={'create'}
            onClick={async () => {
              const { data } = await createWorkspace({
                variables: {
                  isOpen: newWorkspaceIsOpen,
                  name,
                  tags
                }
              })
              navigateTo(
                `/workspace/${data?.admin?.createWorkspace?.id}/manage`
              )
              setName('')
              hideNavigation()
              setModalShown(false)
            }}
            disabled={name.length === 0}
          >
            <Trans>Create</Trans>
          </ButtonPrimary>
        ]}
      >
        <FormRow mb="24px">
          <Label htmlFor="createWorkspaceInput">
            <Trans>Workspace name</Trans>
          </Label>
          <Input
            fullWidth
            id="createWorkspaceInput"
            placeholder={t`My new workspace`}
            onChange={(val) => {
              setName(val)
            }}
            inputRef={inputEl}
            value={name}
          />
        </FormRow>

        <FormRow mb="24px">
          <Label>
            <Trans>Visibility</Trans>
          </Label>

          <HorizontalChoiceSelector
            name="type"
            onChange={(val) => {
              setNewWorkspaceIsOpen(val)
            }}
            options={options}
            value={newWorkspaceIsOpen}
          />
          <div className="form-hint">
            <Trans>
              Note: <em>These settings can be changed later</em>
            </Trans>
          </div>
        </FormRow>

        <FormRow>
          <Label>
            <Trans>Category tags</Trans>
          </Label>
          <AccessibilityLabel text={t`Category tags`}>
            <TagSelect
              defaultValue={[]}
              autoCreate={false}
              autoSave={false}
              onChange={(value) => changeTags(value)}
            />
          </AccessibilityLabel>
        </FormRow>
        <FormRow>
          <div className="form-hint">
            <Trans>Use this for categorisation of Workspaces</Trans>
          </div>
        </FormRow>
      </Modal>
    </>
  )
}
